// Native push for the Android shell (FCM), the counterpart to lib/push.js's Web Push.
//
// Android's WebView has no Web Push (no Notification / serviceWorker / PushManager), so inside the
// Capacitor shell we use the native @capacitor/push-notifications plugin instead. Like the custom
// HealthConnect plugin, it's reached at runtime via window.Capacitor.Plugins.PushNotifications (the
// native bridge registers it globally) — the zero-build web app never imports the npm package.
//
// notify.js / push.js delegate here when running natively, so Profile's Settings card and the
// reminder/follow toggles work in the app exactly as Web Push does on the PWA. The device token is
// handed to the server (api.fcmRegister) and stored there in fcm_tokens; sendToUser fans out to it.

import { api } from "../api.js";

const TOKEN_KEY = "fcm_token"; // last token we registered, so we can unregister it server-side.

/** The native PushNotifications plugin, or null on plain web / before the APK ships it. */
function plugin() {
  return (typeof window !== "undefined" && window.Capacitor?.Plugins?.PushNotifications) || null;
}

/** True only inside the native Android shell. */
export function isNative() {
  return typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
}

/** True when native push is actually callable — gates the Settings notification UI on native. */
export function nativePushSupported() {
  return isNative() && !!plugin();
}

/** Whether we currently hold a registered FCM token (our sync proxy for "permission granted"). */
export function isNativePushEnabled() {
  try { return !!localStorage.getItem(TOKEN_KEY); } catch { return false; }
}

/**
 * Request OS notification permission, register with FCM, and hand the token to the server.
 * Resolves with the token; throws if the user denies or registration fails/times out.
 */
export async function enableNativePush() {
  const p = plugin();
  if (!p) throw new Error("Native push isn't available here.");

  let perm = await p.checkPermissions();
  if (perm.receive !== "granted") perm = await p.requestPermissions();
  if (perm.receive !== "granted") throw new Error("Notifications were not allowed");

  const token = await registerForToken(p);
  if (!token) throw new Error("No push token returned");
  try { localStorage.setItem(TOKEN_KEY, token); } catch {}
  await api.fcmRegister(token);
  return token;
}

/**
 * Make sure this device has a live FCM token on the server, without prompting for anything.
 *
 * Registration used to happen only when a notification toggle was switched ON — but an account
 * that had already enabled friend/reminder notifications on the PWA arrives in the app with
 * every toggle showing ON, so nothing ever ran and `fcm_tokens` stayed empty: the settings said
 * notifications were on while no push could physically reach the phone. Called on every boot,
 * this closes that gap and doubles as token-rotation upkeep (the server upserts, so it's cheap
 * and idempotent). Silent by design: it registers only if the OS grant already exists (the shell
 * asks for POST_NOTIFICATIONS at launch), never by raising a permission dialog of its own.
 */
export async function ensureNativePushRegistered() {
  const p = plugin();
  if (!p) return false;
  try {
    const perm = await p.checkPermissions();
    if (perm?.receive !== "granted") return false;
    const token = await registerForToken(p);
    if (!token) return false;
    try { localStorage.setItem(TOKEN_KEY, token); } catch {}
    await api.fcmRegister(token);
    return true;
  } catch {
    return false; // offline / plugin hiccup — the next launch tries again
  }
}

/** register() and resolve the one "registration" event it fires (or reject on error/timeout). */
function registerForToken(p) {
  let regH, errH, timer;
  const cleanup = async () => {
    clearTimeout(timer);
    try { (await regH)?.remove?.(); } catch {}
    try { (await errH)?.remove?.(); } catch {}
  };
  return new Promise((resolve, reject) => {
    timer = setTimeout(() => reject(new Error("Push registration timed out")), 15000);
    regH = p.addListener("registration", (t) => resolve(t?.value || t?.token));
    errH = p.addListener("registrationError", (e) => reject(new Error(e?.error || "Push registration failed")));
    p.register();
  }).finally(cleanup);
}

/** Drop our token server-side and locally. Best-effort. */
export async function disableNativePush() {
  let token = null;
  try { token = localStorage.getItem(TOKEN_KEY); } catch {}
  if (token) await api.fcmUnregister(token).catch(() => {});
  try { localStorage.removeItem(TOKEN_KEY); } catch {}
}

/**
 * Wire notification taps → in-app navigation (call once at app start). When the user taps an FCM
 * notification, Capacitor fires pushNotificationActionPerformed with the data we sent (incl. url).
 */
export function initNativePushNavigation(navigate) {
  const p = plugin();
  if (!p || typeof navigate !== "function") return;
  p.addListener("pushNotificationActionPerformed", (action) => {
    const url = action?.notification?.data?.url;
    if (url) navigate(url);
  });
}
